<script setup>
import { computed } from 'vue'
import { Icon } from '@iconify/vue'

/**
 * AppCard — content card: media, leading, title, description, footer.
 * The anatomy follows Nuxt UI's PageCard, on Card's clipped surface.
 *
 * Variants:
 *   outline       — white, bordered, clipped surface with padded content
 *   naked         — the anatomy alone, no border, fill or padding (rows of a list)
 *   soft-gradient — the tinted gradient surface with a faint stroke (a feature card)
 *
 * Sizes: md — text-base title, text-sm description · sm — text-sm title, text-xs description (rows of a list)
 *
 * Align: start — the leading beside the title and description · center — the leading above them, everything centred (a tile)
 *
 * Slots:
 *   #default     — media, edge to edge; under the content, or over it with `reverse`
 *   #header      — above the leading, title and description, inside the padding (a preview that keeps the card's inset)
 *   #leading     — beside the title and description; falls back to `icon`
 *   #title       — replaces the title text; a row, so what follows a heading lands on the right
 *   #description — replaces the description text
 *   #trailing    — on the right, centred on the title and description (a link card's chevron)
 *   #footer      — pinned to the card's bottom edge
 */
const props = defineProps({
  as:          { type: String,  default: 'div' },
  variant:     { type: String,  default: 'outline', validator: (variant) => ['outline', 'naked', 'soft-gradient'].includes(variant) },
  icon:        { type: String,  default: null },          // Iconify name, drawn as the leading
  title:       { type: String,  default: '' },
  description: { type: String,  default: '' },
  reverse:     { type: Boolean, default: false },         // media first
  highlight:   { type: Boolean, default: false },         // tint border + glow, e.g. the current step (outline only)
  hoverable:   { type: Boolean, default: false },         // gray-100 fill under the pointer, Figma's Hover state (outline only)
  to:          { type: [String, Object], default: null }, // the whole card links there; `title` names the link
  href:        { type: String,  default: null },          // the whole card opens this outside page in a new tab; `title` names the link
  desktopLink: { type: Boolean, default: false },         // `to` / `href` on desktop only, a pointer shortcut to the footer's own link; hovers gray-100 + glow there
  size:        { type: String,  default: 'md', validator: (size) => ['md', 'sm'].includes(size) },
  align:       { type: String,  default: 'start', validator: (align) => ['start', 'center'].includes(align) },
})

const isNaked = computed(() => props.variant === 'naked')
const isSoftGradient = computed(() => props.variant === 'soft-gradient')
const isSmall = computed(() => props.size === 'sm')
const isCentered = computed(() => props.align === 'center')

// A naked card has no surface to border, fill, clip or highlight.
const surfaceClass = computed(() => {
  if (isNaked.value) return ''
  // Figma's "Gradient / Soft" fill and "Soft Stroke": blue-50 running into two tints the palette lacks.
  if (isSoftGradient.value) return 'overflow-hidden border border-[#dbe4f3]/60 bg-gradient-to-r from-blue-50 via-[#f0f2ff] via-[57.9%] to-[#f3f0ff]'
  return [
    'overflow-hidden border bg-white',
    props.highlight ? 'border-tint-1 shadow-[0_0_2px_#69a0ff]' : 'border-gray-200',
    { 'transition-colors hover:bg-gray-100': props.hoverable },
    // Figma's hover for a card that is a link on desktop: the gray fill and the focus glow, only where the link is.
    { 'transition no-touch:hover:bg-gray-100 no-touch:hover:shadow-[0_0_2px_#69a0ff]': props.desktopLink },
  ]
})

// The card's own link: an outside page opens in a new tab, a route goes through RouterLink.
const cardLink = computed(() => {
  if (props.href) return { is: 'a', attrs: { href: props.href, target: '_blank', rel: 'noopener noreferrer' } }
  if (props.to) return { is: 'RouterLink', attrs: { to: props.to } }
  return null
})
</script>

<template>
  <component
    :is="as"
    class="relative flex flex-col rounded-xl has-[>a:focus-visible]:ring-2 has-[>a:focus-visible]:ring-blue-300"
    :class="surfaceClass"
  >
    <div class="flex flex-1 flex-col" :class="{ 'p-3.5': !isNaked, 'order-last': reverse, 'justify-center': isCentered }">
      <!-- Inside the padding, unlike the media slot, and 8px above the title, as the title is above the description. -->
      <div v-if="$slots.header" class="mb-2">
        <slot name="header" />
      </div>

      <!-- Centred, the leading stacks above the text instead of sitting beside it, and the stack sits mid-height in a tile stretched by its row. -->
      <div class="flex" :class="isCentered ? 'flex-col items-center gap-y-2 text-center' : 'gap-x-3'">
        <div v-if="icon || $slots.leading" class="flex-none">
          <slot name="leading">
            <Icon :icon="icon" class="my-0.5 size-5 text-tint-1" />
          </slot>
        </div>

        <!-- Centred on the row, so a one-line title sits mid-height beside a taller leading instead of on its top edge. -->
        <div class="flex min-w-0 flex-1 flex-col justify-center gap-y-2" :class="{ 'items-center': isCentered }">
          <div
            v-if="title || $slots.title"
            class="flex items-center gap-x-2 font-semibold text-gray-900"
            :class="[isSmall ? 'text-sm' : 'text-base', isCentered ? 'justify-center' : 'justify-between']"
          >
            <slot name="title">{{ title }}</slot>
          </div>

          <div v-if="description || $slots.description" class="text-gray-600" :class="isSmall ? 'text-xs' : 'text-sm'">
            <slot name="description">{{ description }}</slot>
          </div>
        </div>

        <div v-if="$slots.trailing" class="flex-none self-center">
          <slot name="trailing" />
        </div>
      </div>

      <div v-if="$slots.footer" class="mt-auto pt-3.5">
        <slot name="footer" />
      </div>
    </div>

    <slot />

    <!-- A desktop link is for the pointer only: the footer's own link stays the one keyboards, screen readers and touch screens use. -->
    <component
      :is="cardLink.is"
      v-if="cardLink"
      v-bind="cardLink.attrs"
      :aria-label="desktopLink ? undefined : title || undefined"
      :aria-hidden="desktopLink || undefined"
      :tabindex="desktopLink ? -1 : undefined"
      class="absolute inset-0"
      :class="{ 'touch:hidden': desktopLink }"
    />
  </component>
</template>
